/**
 * Mock Agents for Local Development
 *
 * When running on localhost, the remote Express API requires OIDC auth, which
 * won't complete against http://localhost:3000 (no registered redirect URI).
 * That leaves the agents list empty and blocks UI/CSS work.
 *
 * This module provides a toggle that surfaces the canonical agents list to the
 * frontend without going through the backend. The agent metadata itself is
 * derived from the SAME source of truth the backend uses —
 * apps/express-api/config/agents.json — copied into the frontend bundle at
 * build time by `npm run sync-agents-config` (which runs automatically on
 * prestart/predev/prebuild/pretest hooks defined in package.json).
 *
 * IMPORTANT: No agent fields (version, sampleQuestions, descriptions, etc.)
 * are hardcoded in this file. They all flow from agents.json so there is a
 * single source of truth.
 *
 * Usage (from the browser console on localhost):
 *   window.askAldo.mockAgents.enable()   // turn on, then refresh the page
 *   window.askAldo.mockAgents.disable()  // turn off
 *   window.askAldo.mockAgents.isEnabled()
 *
 * Or set REACT_APP_MOCK_AGENTS=true in apps/react-app/.env.development.local.
 *
 * NOTE: This only affects the local dev bundle. It does nothing in production
 * or anywhere off localhost.
 */

import type { BackendAgent, AgentsResponse } from '../services/api';
// Generated from apps/express-api/config/agents.json at build time.
// See apps/react-app/scripts/sync-agents-config.js.
import agentsConfig from '../config/agents.generated.json';

const MOCK_AGENTS_KEY = 'askaldo_mock_agents_enabled';

/**
 * Convert the canonical agents.json structure into the BackendAgent shape the
 * frontend expects from the live API.
 *
 * Keeps hidden agents (e.g. Aldo orchestrator) because the real API also returns
 * them via ?includeHidden=true and App.tsx needs them for routing.
 * Only filters disabled agents.
 */
const buildMockAgents = (): BackendAgent[] => {
  const raw = (agentsConfig as { agents?: unknown[] }).agents;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((a): a is BackendAgent => !!a && typeof (a as { id?: unknown }).id === 'string')
    .filter(a => !a.disabled);
};

const MOCK_AGENTS: BackendAgent[] = buildMockAgents();

const isLocalhost = (): boolean => {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h.startsWith('192.168.') || h.endsWith('.local');
};

/** Is the mock-agents fallback currently active? Only honored on localhost. */
export const isMockAgentsEnabled = (): boolean => {
  if (!isLocalhost()) return false;
  if (process.env.REACT_APP_MOCK_AGENTS === 'true') return true;
  try {
    return localStorage.getItem(MOCK_AGENTS_KEY) === 'true';
  } catch {
    return false;
  }
};

export const enableMockAgents = (): void => {
  if (!isLocalhost()) {
    console.warn('[mockAgents] only available on localhost');
    return;
  }
  try {
    localStorage.setItem(MOCK_AGENTS_KEY, 'true');
    console.log('[mockAgents] ENABLED — refresh the page to load the mock agent list');
  } catch (e) {
    console.error('[mockAgents] failed to enable:', e);
  }
};

export const disableMockAgents = (): void => {
  try {
    localStorage.removeItem(MOCK_AGENTS_KEY);
    console.log('[mockAgents] disabled — refresh the page to hit the real API');
  } catch (e) {
    console.error('[mockAgents] failed to disable:', e);
  }
};

/** Return a fake AgentsResponse that matches the shape the real API would return. */
export const getMockAgentsResponse = (): AgentsResponse => ({
  agents: MOCK_AGENTS,
  user: {
    email: 'dev-local@unifirst.com',
    name: 'Local Dev',
    roles: ['authenticated', 'admin', 'super-admin'],
    accessibleAgents: MOCK_AGENTS.map(a => a.id),
  },
  gateway: 'local-mock',
});

if (typeof window !== 'undefined' && isLocalhost()) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  w.askAldo = w.askAldo || {};
  w.askAldo.mockAgents = {
    enable: enableMockAgents,
    disable: disableMockAgents,
    isEnabled: isMockAgentsEnabled,
    list: () => MOCK_AGENTS.map(a => ({ id: a.id, name: a.name, version: a.version })),
  };

  if (isMockAgentsEnabled()) {
    console.log(
      `%c[mockAgents] active — frontend is using ${MOCK_AGENTS.length} agents from agents.json (sync-agents-config)`,
      'color:#00b194;font-weight:bold',
    );
  } else {
    console.log(
      `[mockAgents] available on localhost (${MOCK_AGENTS.length} agents synced from agents.json). Run window.askAldo.mockAgents.enable() then refresh to preview the UI without a backend.`,
    );
  }
}
